import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import ReactMapGL from 'react-map-gl';
import { Map } from 'immutable';
import get from 'lodash/get';

import addCustomAttribution from 'components/shared/Map/mapAttribution';
import { zoomToDatapoints, zoomToBounds } from 'components/shared/Map/zoomToPoints';
import showPopup, { createPopupData } from 'components/shared/Map/showPopup';
import {
  CHOROPLETH_LAYER_ID,
  CHOROPLETH_VALUE_PROPERTY,
} from 'components/datasets/EditMapDatasets/layers/boundaries';
import { createGradient } from 'components/maps/EditMap/HeatmapLayer/heatmapLayer';
import BoundButtons from 'components/maps/EditMapBound/EditMapBoundsList/BoundButtons';
import EditMapBoundContextMenu from 'components/maps/EditMapBound/EditMapBoundContextMenu';
import { StyledPopup, StyledTitleContainer } from 'components/maps/EditMapBound/styles';
import ExportDataButton from 'components/maps/ExportData/container';
import MapCustomization from 'components/maps/MapCustomization';
import {
  MapControl,
  PresentMapButton,
  ToggleExportSettingsButton,
  EditMapBound,
  EditMapPopup,
  initialMapSettings,
} from 'components';

import { selectPointEditMap } from 'state/pointEditMap/actions';
import { changeExportSettings } from 'state/exportSettings/actions';
import { selectBoundAction } from 'state/selectBounds/actions';

import { accessToken } from 'utils/constants';
import { MapStyleFormContext, createMapStyleFormProps } from 'contexts/MapStyleFormContext';

import HeatmapLayer from './HeatmapLayer';
import getMapSize from './EditMapSizeCreator';
import { getInitialCoordinates } from './utils/coordinatesCreator';

import {
  StyledMapWrapper,
  ChoroplethLegend,
  ChoroplethLegendRamp,
  ChoroplethLegendScale,
} from './styles';


const mergeMapDatasetsDatapoints = (mapDatasets) => {
  return mapDatasets.reduce((accumulator, mapDataset) => {
    const features = get(mapDataset, 'datapointsGeojson.features', []);
    accumulator.features = [...accumulator.features, ...features];

    return accumulator;
  }, { type: 'FeatureCollection', features: [] });
};

const formatChoroplethValue = (value) => {
  if (value === null || value === undefined) {
    return 'No data';
  }

  const number = Number(value);

  if (Number.isNaN(number)) {
    return String(value);
  }

  return Number.isInteger(number)
    ? number.toLocaleString()
    : number.toLocaleString(undefined, { maximumFractionDigits: 2 });
};

const createLegendGradientBackground = (gradientStops, minimum, maximum) => {
  if (gradientStops.length === 0) {
    return 'linear-gradient(90deg, #dbeafe 0%, #1d4ed8 100%)';
  }

  if (minimum === maximum) {
    return `linear-gradient(90deg, ${gradientStops[1]} 0%, ${gradientStops[gradientStops.length - 1]} 100%)`;
  }

  return `linear-gradient(90deg, ${gradientStops.map((stop, index) => (
    index % 2 === 0
      ? null
      : `${stop} ${Math.round(((gradientStops[index - 1] - minimum) / (maximum - minimum)) * 100)}%`
  )).filter(Boolean).join(', ')})`;
};

const isPinLayerId = layerId => (
  typeof layerId === 'string'
  && (
    layerId.startsWith('point')
    || layerId.startsWith('clusters')
    || layerId.startsWith('cluster-count')
    || layerId === 'circle-point'
  )
);

const ChoroplethPopup = ({ selectedBoundary, onClose }) => {
  if (!selectedBoundary) {
    return null;
  }

  const {
    latitude,
    longitude,
    boundaryName,
    aggregateName,
    value,
  } = selectedBoundary;

  return (
    <StyledPopup
      latitude={latitude}
      longitude={longitude}
      closeButton
      closeOnClick={false}
      anchor="bottom"
      onClose={onClose}
    >
      <StyledTitleContainer>
        <strong>{boundaryName}</strong>
      </StyledTitleContainer>
      <div style={{ padding: '10px 12px', minWidth: 180 }}>
        <div style={{ color: '#6b7280', fontSize: 12, marginBottom: 4 }}>{aggregateName}</div>
        <div style={{ fontSize: 18, fontWeight: 700 }}>{formatChoroplethValue(value)}</div>
      </div>
    </StyledPopup>
  );
};

const ChoroplethLegendOverlay = ({ visualizationType, aggregates = [], heatmapGradient }) => {
  if (visualizationType !== 'CHOROPLETH') {
    return null;
  }

  const visualizedAggregate = aggregates.find(({ isVisualized }) => isVisualized);
  const totals = get(visualizedAggregate, 'totals.mapShapes', []);
  const values = totals
    .map(({ value, total }) => Number(value === undefined ? total : value))
    .filter(value => !Number.isNaN(value));

  if (!visualizedAggregate || values.length === 0) {
    return null;
  }

  const minimum = Math.min(...values);
  const maximum = Math.max(...values);
  const midpoint = minimum + ((maximum - minimum) / 2);
  const gradientStops = createGradient(heatmapGradient, [minimum, midpoint, maximum]);
  const legendBackground = createLegendGradientBackground(gradientStops, minimum, maximum);

  return (
    <ChoroplethLegend>
      <div style={{ marginBottom: 10, fontSize: 13, fontWeight: 700, color: '#111827' }}>
        {visualizedAggregate.name}
      </div>
      <ChoroplethLegendRamp background={legendBackground} />
      <ChoroplethLegendScale>
        <span>{formatChoroplethValue(minimum)}</span>
        <span>{formatChoroplethValue(maximum)}</span>
      </ChoroplethLegendScale>
    </ChoroplethLegend>
  );
};

class EditMap extends Component {
  constructor(props) {
    super(props);

    const { mapStyles } = props;

    const { zoom } = initialMapSettings;
    const [longitude, latitude] = getInitialCoordinates(mapStyles);
    this.mapRef = React.createRef();

    this.state = {
      viewport: {
        latitude,
        longitude,
        zoom,
      },
      canOpenContextMenu: false,
      selectedBoundary: null,
      isHoveringPin: false,
    };
  }

  componentDidMount() {
    const { changeExportSettings, name } = this.props;

    this.zoomToPoints();
    changeExportSettings({ mapName: name });

    const mapInstance = get(this.mapRef, 'current', false);

    if (mapInstance) {
      addCustomAttribution(mapInstance);
    }

  }

  componentDidUpdate(prev) {
    const { mapStyles } = this.props;
    const pointsUpdated = prev.mapStyles.getIn(['sources']).size !== mapStyles.getIn(['sources']).size;
    const boundariesList = mapStyles.getIn(['sources', 'boundaries', 'data', 'features'], Map());
    const prevBoundariesList = prev.mapStyles.getIn(['sources', 'boundaries', 'data', 'features'], Map());
    const boundariesData = mapStyles.getIn(['sources', 'boundaries', 'data'], null);
    const prevBoundariesData = prev.mapStyles.getIn(['sources', 'boundaries', 'data'], null);

    if (boundariesList.size !== prevBoundariesList.size) {
      this.zoomToPoints();
    }

    if (pointsUpdated) {
      this.zoomToPoints();
    }

    if (prev.visualizationType === 'CHOROPLETH' && this.props.visualizationType !== 'CHOROPLETH') {
      this.clearSelectedBoundary();
    }

    if (boundariesData !== prevBoundariesData) {
      this.syncBoundariesSource(boundariesData);
    }
  }

  componentWillUnmount() {
    // remove a pop up when change the data set
    const { selectPointEditMap, selectBound } = this.props;
    selectPointEditMap({});

    // remove boundaries popup information
    selectBound({});

    this.clearSelectedBoundary();
  }

  get dataAttr() {
    const { viewport } = this.state;
    const [value] = get(viewport, 'center', [0]);

    return Math.floor(value);
  }

  zoomToPoints = () => {
    const { mapStyles } = this.props;
    const setState = this.setState.bind(this);

    zoomToBounds(mapStyles, setState)
    || zoomToDatapoints(mapStyles, setState, 'datapoints-0');
  };

  onViewportChange = ({ width, height, ...viewport }) => {
    this.setState({ viewport });
  };

  setSelectedBoundaryFromFeature = (selectedFeature, lngLat) => {
    const { aggregates = [] } = this.props;
    const visualizedAggregate = aggregates.find(({ isVisualized }) => isVisualized);
    const {
      name: boundaryName,
      [CHOROPLETH_VALUE_PROPERTY]: value,
    } = selectedFeature.properties || {};
    const longitude = Array.isArray(lngLat) ? lngLat[0] : lngLat.lng;
    const latitude = Array.isArray(lngLat) ? lngLat[1] : lngLat.lat;

    this.setState({
      selectedBoundary: {
        latitude,
        longitude,
        boundaryName,
        aggregateName: visualizedAggregate ? visualizedAggregate.name : 'Insight',
        value,
      },
    });
  };

  clearSelectedBoundary = () => {
    if (this.state.selectedBoundary) {
      this.setState({ selectedBoundary: null });
    }
  };

  syncBoundariesSource = (boundariesData) => {
    const mapbox = this.getMapboxInstance();

    if (!mapbox || typeof mapbox.getSource !== 'function') {
      return;
    }

    const boundariesSource = mapbox.getSource('boundaries');

    if (!boundariesSource || typeof boundariesSource.setData !== 'function') {
      return;
    }

    const nextData = boundariesData && typeof boundariesData.toJS === 'function'
      ? boundariesData.toJS()
      : boundariesData;

    if (nextData) {
      boundariesSource.setData(nextData);
    }
  };

  getMapboxInstance = () => {
    const mapRef = get(this.mapRef, 'current', null);

    if (!mapRef) {
      return null;
    }

    return typeof mapRef.getMap === 'function' ? mapRef.getMap() : mapRef;
  };

  getEventPoint = (event, mapbox) => {
    if (Array.isArray(event.point)) {
      return event.point;
    }

    if (event.point && typeof event.point.x === 'number' && typeof event.point.y === 'number') {
      return [event.point.x, event.point.y];
    }

    if (mapbox && event.lngLat) {
      const projectedPoint = mapbox.project(event.lngLat);

      if (projectedPoint) {
        return [projectedPoint.x, projectedPoint.y];
      }
    }

    return null;
  };

  showEditMapPopup = (event) => {
    const isPrimaryClick = event.leftButton
      || get(event, 'srcEvent.button') === 0
      || get(event, 'originalEvent.button') === 0
      || get(event, 'nativeEvent.button') === 0;

    if (!isPrimaryClick) {
      return false;
    }

    const { visualizationType, selectPointEditMap, selectBound } = this.props;
    const mapDatasets = get(this.props, 'mapDatasets', []);

    if (visualizationType === 'CHOROPLETH') {
      const mapbox = this.getMapboxInstance();
      const point = mapbox ? this.getEventPoint(event, mapbox) : null;
      const clickedFeature = point
        ? mapbox.queryRenderedFeatures(point, { layers: [CHOROPLETH_LAYER_ID] })[0]
        : null;

      if (clickedFeature) {
        selectPointEditMap({});
        selectBound({});
        this.closeContextMenu();
        this.setSelectedBoundaryFromFeature(clickedFeature, event.lngLat);

        return;
      }
    }

    const datapointsGeojson = mergeMapDatasetsDatapoints(mapDatasets);

    const popupData = createPopupData({ event, datapointsGeojson });

    if (popupData) {
      this.clearSelectedBoundary();
      this.closeContextMenu();
      selectBound(false);

      showPopup(selectPointEditMap, popupData);

      return;
    }

    // close popups on outside click
    this.clearSelectedBoundary();
    selectPointEditMap({});
    this.closeContextMenu();
  };

  handleMapHover = (event) => {
    const hoveredPin = get(event, 'features', []).length > 0;

    if (hoveredPin !== this.state.isHoveringPin) {
      this.setState({ isHoveringPin: hoveredPin });
    }
  };

  handleContextMenu = (event) => {
    event.preventDefault();

    const { selectBound } = this.props;
    const isContextMenuClick = event.target.className !== 'context-menu-item';

    if (isContextMenuClick) {
      // show a context menu
      this.setState({ canOpenContextMenu: true, selectedMapBoundPopup: event });
      // close boundaries popup
      selectBound({});
    }
  };

  closeContextMenu = () => {
    this.setState({ canOpenContextMenu: false });
  };

  render() {
    const {
      color,
      mapShapes,
      mapStyles,
      mapDatasets,
      exportSettings,
      heatmapGradient,
      heatmapContrast,
      heatmapVisibility,
      visualizationType,
      selectedDataPoint,
      presentMapEnabled,
      heatmapWeightField,
      aggregatableFields,
    } = this.props;
    const {
      viewport,
      canOpenContextMenu,
      selectedMapBoundPopup,
      selectedBoundary,
      isHoveringPin,
    } = this.state;
    const { width, height } = getMapSize(exportSettings, presentMapEnabled);
    const datapointsGeojson = mergeMapDatasetsDatapoints(mapDatasets);
    const mapStylesJS = typeof mapStyles.toJS === 'function' ? mapStyles.toJS() : mapStyles;
    const interactiveLayerIds = get(mapStylesJS, 'layers', [])
      .map(layer => layer.id)
      .filter(isPinLayerId);

    return (
      <StyledMapWrapper
        id="editMapsMap"
        data-map-center={this.dataAttr}
        presentMapEnabled={presentMapEnabled}
      >
        <ReactMapGL
          attributionControl={false}
          ref={this.mapRef}
          preserveDrawingBuffer
          width={width}
          height={height}
          onViewportChange={this.onViewportChange}
          onHover={this.handleMapHover}
          onClick={this.showEditMapPopup}
          onContextMenu={this.handleContextMenu}
          interactiveLayerIds={interactiveLayerIds}
          getCursor={() => (isHoveringPin ? 'pointer' : 'default')}
          mapboxApiAccessToken={accessToken}
          mapStyle={mapStyles}
          {...viewport}
        >
          <MapControl />
          <HeatmapLayer
            color={color}
            heatmapGradient={heatmapGradient}
            heatmapContrast={heatmapContrast}
            heatmapVisibility={heatmapVisibility}
            datapointsGeojson={datapointsGeojson}
            visualizationType={visualizationType}
            heatmapWeightField={heatmapWeightField}
            aggregatableFields={aggregatableFields}
          />
          <MapStyleFormContext.Provider value={createMapStyleFormProps(this.props)}>
            <MapCustomization />
          </MapStyleFormContext.Provider>
          <EditMapPopup
            color={color}
            showEditButtons={false}
            selectedDataPoint={selectedDataPoint}
          />
          <ChoroplethPopup
            selectedBoundary={selectedBoundary}
            onClose={this.clearSelectedBoundary}
          />
          <ChoroplethLegendOverlay
            visualizationType={visualizationType}
            aggregates={this.props.aggregates}
            heatmapGradient={heatmapGradient}
          />
          <EditMapBoundContextMenu
            closeContextMenu={this.closeContextMenu}
            canOpenContextMenu={canOpenContextMenu}
            selectedBoundPopupInfo={selectedMapBoundPopup}
          />
          <EditMapBound
            renderBoundriesButtons={props => (<BoundButtons mapShapes={mapShapes} {...props} />)}
          />
        </ReactMapGL>
        <PresentMapButton mapShapes={mapShapes} />
        <ExportDataButton />
        <ToggleExportSettingsButton mapDatasets={mapDatasets} />
      </StyledMapWrapper>
    );
  }
}

EditMap.propTypes = {
  presentMapEnabled: PropTypes.bool,
  color: PropTypes.string.isRequired,
  basemap: PropTypes.string.isRequired,
  mapShapes: PropTypes.array.isRequired,
  mapStyles: PropTypes.object.isRequired,
  mapDatasets: PropTypes.array.isRequired,
  exportSettings: PropTypes.object.isRequired,
  shapeBorderColor: PropTypes.string.isRequired,
  shapeBorderWidth: PropTypes.number.isRequired,
  visualizationType: PropTypes.string.isRequired,
  aggregates: PropTypes.array,
  heatmapWeightField: PropTypes.string,
  heatmapContrast: PropTypes.number,
  heatmapVisibility: PropTypes.number,
  aggregatableFields: PropTypes.array.isRequired,
};

const mapStateToProps = ({
  presentMap,
  exportSettings,
  heatmapSettings,
}) => ({ heatmapSettings, exportSettings, ...presentMap });

const mapDispatchToProps = {
  selectPointEditMap,
  selectBound: selectBoundAction,
  changeExportSettings,
};

export default connect(mapStateToProps, mapDispatchToProps)(EditMap);
